import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle, FileText, PlayCircle, AlignLeft, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { PageHeader, EmptyState, InactiveNotice } from "@/components/dash/Common";
import { FullLoader } from "@/components/ProtectedRoute";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, openFile } from "@/lib/api";
import { isPackageActive, youtubeId } from "@/lib/format";

const TYPE_ICON = { video: PlayCircle, pdf: FileText, text: AlignLeft };

export function StudentCourses() {
  const { user } = useAuth();
  const { data, loading } = useApi("/student/courses");
  if (loading) return <FullLoader />;
  return (
    <div data-testid="student-courses-page">
      <PageHeader eyebrow="Materi" title="Materi, video & modul" description="Pilih mata pelajaran untuk mulai belajar dan tandai materi yang telah selesai." />
      {!isPackageActive(user) && <InactiveNotice />}
      {isPackageActive(user) && data?.length === 0 && <EmptyState text="Belum ada materi untuk paket Anda." />}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {(data || []).map((c) => {
          const pct = c.materials_total ? Math.round((c.materials_done / c.materials_total) * 100) : 0;
          return (
            <Link key={c.id} to={`/siswa/materi/${c.id}`} className="rounded-lg border border-slate-200 bg-white p-6 hover:border-amber-400 transition-colors duration-200" data-testid={`course-card-${c.id}`}>
              <p className="font-display text-lg font-semibold text-slate-900">{c.title}</p>
              <p className="text-sm text-slate-600 mt-2 line-clamp-2">{c.description}</p>
              <div className="mt-6 flex justify-between text-xs text-slate-500"><span>{c.materials_done}/{c.materials_total} materi</span><span className="font-semibold text-slate-900">{pct}%</span></div>
              <Progress value={pct} className="h-1.5 mt-2" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function Viewer({ mat }) {
  const yt = youtubeId(mat.youtube_url);
  return (
    <div className="space-y-4" data-testid="material-viewer">
      <h2 className="font-display text-xl font-semibold text-slate-900">{mat.title}</h2>
      {mat.type === "video" && (yt ? (
        <div className="aspect-video rounded-lg overflow-hidden bg-slate-900"><iframe className="h-full w-full" src={`https://www.youtube.com/embed/${yt}`} title={mat.title} allowFullScreen /></div>
      ) : mat.youtube_url ? <a href={mat.youtube_url} target="_blank" rel="noreferrer" className="text-amber-600 underline">Buka video</a> : <p className="text-sm text-slate-500">Video belum tersedia.</p>)}
      {mat.type === "pdf" && (mat.file_id ? (
        <Button onClick={() => openFile(mat.file_id).catch((e) => toast.error(errMsg(e)))} className="bg-slate-900" data-testid="open-pdf-button"><ExternalLink className="h-4 w-4 mr-2" />Buka modul: {mat.file_name}</Button>
      ) : <p className="text-sm text-slate-500">File modul belum diunggah.</p>)}
      {mat.content && <p className="text-slate-700 leading-relaxed whitespace-pre-line">{mat.content}</p>}
    </div>
  );
}

export function StudentCourseDetail() {
  const { id } = useParams();
  const { data, loading, error, reload } = useApi(`/student/courses/${id}`);
  const [activeId, setActiveId] = useState(null);
  if (loading) return <FullLoader />;
  if (error) return <EmptyState text={error} />;
  const active = data.materials.find((m) => m.id === activeId) || data.materials[0];
  const toggle = async (mid) => {
    try {
      const { data: r } = await api.post(`/student/materials/${mid}/toggle`);
      toast.success(r.completed ? "Ditandai selesai" : "Tanda selesai dibatalkan");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const pct = data.materials.length ? Math.round((data.done.length / data.materials.length) * 100) : 0;
  return (
    <div data-testid="course-detail-page">
      <Link to="/siswa/materi" className="text-sm text-slate-500 flex items-center gap-1 mb-4 hover:text-slate-900" data-testid="back-to-courses-link"><ArrowLeft className="h-4 w-4" />Semua materi</Link>
      <PageHeader eyebrow={`Progres ${pct}%`} title={data.course.title} description={data.course.description} />
      {data.materials.length === 0 ? <EmptyState text="Belum ada materi di kurikulum ini." /> : (
        <div className="grid lg:grid-cols-12 gap-6">
          <ul className="lg:col-span-4 rounded-lg border border-slate-200 bg-white divide-y divide-slate-100 h-fit">
            {data.materials.map((m) => {
              const Icon = TYPE_ICON[m.type] || AlignLeft;
              const done = data.done.includes(m.id);
              return (
                <li key={m.id} className={`flex items-center gap-3 p-4 cursor-pointer ${active?.id === m.id ? "bg-amber-50" : "hover:bg-slate-50"}`} onClick={() => setActiveId(m.id)} data-testid={`material-item-${m.id}`}>
                  <Icon className="h-4 w-4 text-slate-500 shrink-0" />
                  <span className="text-sm flex-1">{m.title}</span>
                  <button onClick={(e) => { e.stopPropagation(); toggle(m.id); }} data-testid={`material-toggle-${m.id}`}>
                    {done ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <Circle className="h-5 w-5 text-slate-300" />}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="lg:col-span-8 rounded-lg border border-slate-200 bg-white p-6">
            {active && <Viewer mat={active} />}
            {active && (
              <Button variant="outline" className="mt-6" onClick={() => toggle(active.id)} data-testid="mark-complete-button">
                {data.done.includes(active.id) ? "Batalkan tanda selesai" : "Tandai selesai"}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
